import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import {
  Upload, User, Building2, Phone, Mail, Link as LinkIcon,
  CheckCircle, AlertCircle, Loader2, Eye
} from 'lucide-react';

/* ─── Live Ad Preview — exact markup from WardPage ─────────── */
const AdPreview = ({ ad }) => {
  const hasContent = ad.business_name || ad.expert_name || ad.description || ad.image_url;
  return (
    <div className="sticky top-6">
      <div className="flex items-center gap-2 mb-3">
        <Eye className="w-4 h-4 text-[#C5A52A]" />
        <p className="text-[#C5A52A] font-mono text-xs uppercase tracking-[0.2em]">Live Preview — Ward Page</p>
      </div>

      {/* Ward-page ad replica */}
      <div className="bg-[#0E0E0E] border border-[#4D4732]/20">
        <div className="flex flex-col md:flex-row items-stretch min-h-[200px]">
          {/* Image */}
          <div className="md:w-1/3 relative min-h-[200px] bg-[#111] flex items-center justify-center overflow-hidden">
            {ad.image_url ? (
              <>
                <img
                  className="absolute inset-0 w-full h-full object-cover"
                  src={ad.image_url}
                  alt={ad.business_name || 'Realtor'}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0E0E0E] hidden md:block" />
              </>
            ) : (
              <div className="flex flex-col items-center gap-2 text-gray-700">
                <User className="w-10 h-10" />
                <p className="font-mono text-xs">Your photo here</p>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="md:w-2/3 p-6 md:p-8 flex flex-col justify-center space-y-4">
            <div>
              <span className="text-[#00e639] text-[10px] font-extrabold tracking-[0.4em] uppercase mb-2 block italic">
                Exclusive Partnership
              </span>
              <h3 className="text-2xl md:text-3xl font-extrabold tracking-tighter uppercase leading-none text-white">
                {ad.business_name || <span className="text-gray-700">Your Brokerage Name</span>}
              </h3>
              {ad.expert_name && (
                <p className="text-[#999077] text-xs font-bold tracking-widest uppercase mt-2">
                  FEATURED EXPERT: <span className="text-[#FFD700]">{ad.expert_name}</span>
                </p>
              )}
            </div>

            {ad.description && (
              <p className="text-[#d0c6ab] font-light text-sm max-w-xl leading-relaxed whitespace-pre-line">
                {ad.description}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4 pt-1">
              {ad.cta_url && (
                <span className="px-6 py-2.5 bg-[#E5E2E1] text-[#0E0E0E] text-[10px] font-extrabold tracking-widest uppercase">
                  {ad.cta_label || 'VIEW LISTINGS'}
                </span>
              )}
              <div className="flex items-center gap-2">
                <span className="text-[#00e639] text-sm">✓</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#999077]">Top 1% Regina Realtors</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard mini-ad preview */}
      <div className="mt-4">
        <p className="text-gray-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-2">Dashboard Mini-Ad Preview</p>
        <div className="bg-[#111] border border-[#2a2a2a] p-3 flex items-center gap-3">
          <div className="w-12 h-12 rounded bg-[#0a0a0a] flex-shrink-0 overflow-hidden">
            {ad.image_url
              ? <img src={ad.image_url} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center"><User className="w-5 h-5 text-gray-700" /></div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-display text-xs truncate">{ad.business_name || 'Brokerage Name'}</p>
            {ad.expert_name && <p className="text-[#C5A52A] font-mono text-[10px]">FEATURED: {ad.expert_name}</p>}
            {ad.description && <p className="text-gray-400 text-[10px] mt-0.5 line-clamp-1">{ad.description}</p>}
          </div>
          {ad.cta_url && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-[#E5E2E1] text-[#0E0E0E] px-2 py-1 whitespace-nowrap flex-shrink-0">
              {ad.cta_label || 'LEARN MORE'}
            </span>
          )}
        </div>
      </div>

      {!hasContent && (
        <p className="text-gray-700 font-mono text-xs text-center mt-4">Fill in the form to see your ad come to life →</p>
      )}
    </div>
  );
};

/* ─── Main Setup Page ───────────────────────────────────────── */
const RealtorSetup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const sessionId = searchParams.get('session_id');
  const wardParam  = searchParams.get('ward');

  const [verifying, setVerifying]       = useState(true);
  const [sessionData, setSessionData]   = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [uploading, setUploading]       = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);

  const [form, setForm] = useState({
    realtor_name: '',
    brokerage:    '',
    description:  '',
    image_url:    '',
    cta_label:    'Book a Free Consultation',
    cta_url:      '',
    phone:        '',
    email:        '',
  });

  // Verify session on load
  useEffect(() => {
    if (!sessionId) {
      setSessionError('No session ID found. Please return to the realtors page and complete checkout first.');
      setVerifying(false);
      return;
    }
    api.get(`/billing/verify-realtor-session?session_id=${sessionId}`)
      .then(res => {
        const data = res.data;
        setSessionData(data);
        // Pre-fill from Stripe metadata
        setForm(f => ({
          ...f,
          realtor_name: data.realtor_name || '',
          brokerage:    data.brokerage || '',
          email:        data.realtor_email || '',
        }));
        if (data.already_submitted) {
          setSubmitted(true);
        }
      })
      .catch(err => {
        setSessionError(err.response?.data?.detail || 'Could not verify your payment. Please contact us at ads@accountability.works.');
      })
      .finally(() => setVerifying(false));
  }, [sessionId]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WEBP image');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be under 8MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/billing/upload-realtor-image?session_id=${sessionId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, image_url: res.data.image_url }));
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.realtor_name.trim()) return toast.error('Your name is required');
    if (!form.brokerage.trim())    return toast.error('Brokerage name is required');
    if (!form.description.trim())  return toast.error('Please add a description — this is your pitch');
    if (!form.image_url)           return toast.error('Please upload your photo first');
    setSubmitting(true);
    try {
      await api.post('/billing/submit-realtor-ad', {
        session_id:   sessionId,
        ward:         sessionData?.ward || wardParam,
        realtor_name: form.realtor_name,
        brokerage:    form.brokerage,
        description:  form.description,
        image_url:    form.image_url,
        cta_label:    form.cta_label,
        cta_url:      form.cta_url,
        phone:        form.phone,
        email:        form.email,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again or email ads@accountability.works.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  // ── States ──────────────────────────────────────────────────
  if (verifying) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-[#C5A52A] animate-spin mx-auto mb-4" />
            <p className="text-gray-400 font-mono text-sm">Verifying your payment...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (sessionError) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-black text-white mb-3">Payment Verification Failed</h2>
            <p className="text-gray-400 mb-6 leading-relaxed">{sessionError}</p>
            <a href="/realtors" className="text-[#C5A52A] font-mono text-sm underline">← Return to Realtors Page</a>
          </div>
        </div>
      </Layout>
    );
  }

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 rounded-full border-2 border-[#C5A52A] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#C5A52A]" />
            </div>
            <h2 className="text-4xl font-black text-white mb-3">You're Submitted.</h2>
            <p className="text-gray-400 mb-2 leading-relaxed">
              Your ad profile for <strong className="text-white">{sessionData?.ward || wardParam}</strong> is in review.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              We'll have it live within 24 hours. You'll hear from us at <strong className="text-gray-300">{form.email || sessionData?.realtor_email}</strong>.
            </p>
            <a href="/" className="inline-block font-mono text-xs text-[#C5A52A] border border-[#C5A52A]/30 px-6 py-3 hover:border-[#C5A52A] transition-colors">
              RETURN TO CYQR
            </a>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Main form ───────────────────────────────────────────────
  return (
    <Layout>
      <div className="min-h-screen bg-[#0D0D0D] text-white">

        {/* Header */}
        <div className="border-b border-[#C5A52A]/20 bg-[#111]">
          <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-[#C5A52A] font-mono text-xs uppercase tracking-widest mb-1">
                <CheckCircle className="w-3.5 h-3.5" />
                <span>Payment confirmed — {sessionData?.ward}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-black">Set Up Your Ward Ad</h1>
            </div>
            <div className="text-right hidden md:block">
              <p className="text-gray-600 font-mono text-xs">Changes update the preview instantly</p>
              <p className="text-gray-700 font-mono text-[10px]">Submit when you're happy with it</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* LEFT: Form */}
            <div className="space-y-6">

              {/* Photo Upload */}
              <div className="bg-[#111] border border-[#1E1E1E] p-6">
                <label className="block font-mono text-xs text-[#C5A52A] uppercase tracking-widest mb-4">
                  Your Photo *
                </label>
                <div
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className={`relative border-2 border-dashed rounded-sm flex flex-col items-center justify-center p-8 cursor-pointer transition-colors ${
                    form.image_url ? 'border-[#C5A52A]/40 bg-[#C5A52A]/5' : 'border-[#2a2a2a] hover:border-[#C5A52A]/30'
                  }`}
                >
                  {form.image_url ? (
                    <div className="text-center">
                      <img src={form.image_url} alt="Preview" className="w-24 h-24 object-cover rounded mx-auto mb-3" />
                      <p className="text-[#C5A52A] font-mono text-xs">Photo uploaded ✓</p>
                      <p className="text-gray-600 text-xs mt-1">Click to replace</p>
                    </div>
                  ) : uploading ? (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-[#C5A52A] animate-spin mx-auto mb-2" />
                      <p className="text-gray-400 font-mono text-xs">Uploading...</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400 text-sm font-medium">Click to upload your photo</p>
                      <p className="text-gray-700 font-mono text-xs mt-1">JPG, PNG or WEBP · max 8MB</p>
                      <p className="text-gray-700 font-mono text-[10px] mt-1">Recommended: professional headshot or team photo</p>
                    </div>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoUpload} />
              </div>

              {/* Name + Brokerage */}
              <div className="bg-[#111] border border-[#1E1E1E] p-6 space-y-4">
                <label className="block font-mono text-xs text-gray-500 uppercase tracking-widest mb-1">Identity</label>
                {[
                  { key: 'realtor_name', label: 'Your Full Name *',    placeholder: 'Jane Smith', icon: <User className="w-4 h-4" /> },
                  { key: 'brokerage',   label: 'Brokerage / Team *',  placeholder: 'Century 21 Dome Realty Inc.', icon: <Building2 className="w-4 h-4" /> },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block font-mono text-xs text-gray-600 uppercase tracking-widest mb-1.5">{field.label}</label>
                    <div className="relative">
                      <div className="absolute left-3 top-3 text-gray-700">{field.icon}</div>
                      <input
                        type="text"
                        value={form[field.key]}
                        onChange={set(field.key)}
                        placeholder={field.placeholder}
                        className="w-full pl-10 pr-4 py-3 bg-[#0D0D0D] border border-[#1E1E1E] text-white text-sm font-mono focus:border-[#C5A52A]/50 outline-none transition-colors placeholder:text-gray-800"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Description */}
              <div className="bg-[#111] border border-[#1E1E1E] p-6">
                <label className="block font-mono text-xs text-gray-600 uppercase tracking-widest mb-1.5">
                  Your Pitch / Description * <span className="text-gray-700 normal-case">(shown on your ad)</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={set('description')}
                  rows={5}
                  placeholder="Tell the residents of your ward who you are, your specialties, years of experience, and why you're the right person to help them buy or sell in this neighbourhood..."
                  className="w-full px-4 py-3 bg-[#0D0D0D] border border-[#1E1E1E] text-white text-sm font-mono focus:border-[#C5A52A]/50 outline-none transition-colors resize-none placeholder:text-gray-800"
                />
                <p className="text-right font-mono text-[10px] text-gray-700 mt-1">{form.description.length} characters</p>
              </div>

              {/* CTA */}
              <div className="bg-[#111] border border-[#1E1E1E] p-6 space-y-4">
                <label className="block font-mono text-xs text-gray-500 uppercase tracking-widest mb-1">
                  Call to Action Button
                </label>
                {[
                  { key: 'cta_label', label: 'Button Text',    placeholder: 'Book a Free Consultation', icon: <Eye className="w-4 h-4" /> },
                  { key: 'cta_url',   label: 'Button Link',    placeholder: 'https://yourwebsite.ca', icon: <LinkIcon className="w-4 h-4" /> },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block font-mono text-xs text-gray-600 uppercase tracking-widest mb-1.5">{field.label}</label>
                    <div className="relative">
                      <div className="absolute left-3 top-3 text-gray-700">{field.icon}</div>
                      <input
                        type="text"
                        value={form[field.key]}
                        onChange={set(field.key)}
                        placeholder={field.placeholder}
                        className="w-full pl-10 pr-4 py-3 bg-[#0D0D0D] border border-[#1E1E1E] text-white text-sm font-mono focus:border-[#C5A52A]/50 outline-none transition-colors placeholder:text-gray-800"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Contact */}
              <div className="bg-[#111] border border-[#1E1E1E] p-6 space-y-4">
                <label className="block font-mono text-xs text-gray-500 uppercase tracking-widest mb-1">
                  Contact Info <span className="text-gray-700 normal-case">(for our records only — not displayed publicly)</span>
                </label>
                {[
                  { key: 'phone', label: 'Phone',           placeholder: '(306) 555-0100', icon: <Phone className="w-4 h-4" /> },
                  { key: 'email', label: 'Contact Email',   placeholder: 'jane@realty.ca', icon: <Mail className="w-4 h-4" /> },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block font-mono text-xs text-gray-600 uppercase tracking-widest mb-1.5">{field.label}</label>
                    <div className="relative">
                      <div className="absolute left-3 top-3 text-gray-700">{field.icon}</div>
                      <input
                        type="text"
                        value={form[field.key]}
                        onChange={set(field.key)}
                        placeholder={field.placeholder}
                        className="w-full pl-10 pr-4 py-3 bg-[#0D0D0D] border border-[#1E1E1E] text-white text-sm font-mono focus:border-[#C5A52A]/50 outline-none transition-colors placeholder:text-gray-800"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Submit */}
              <div className="bg-[#0D0D0D] border border-[#C5A52A]/20 p-6">
                <p className="text-gray-500 text-sm mb-4 leading-relaxed">
                  Once you submit, your ad goes to our team for a quick review. We activate it within 24 hours and let you know. You can contact us at{' '}
                  <a href="mailto:ads@accountability.works" className="text-[#C5A52A] underline">ads@accountability.works</a> if you need any changes after submission.
                </p>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full py-4 font-black uppercase tracking-widest text-sm transition-all disabled:opacity-40"
                  style={{ background: '#C5A52A', color: '#0D0D0D' }}
                  onMouseEnter={e => !submitting && (e.target.style.background = '#D4B93A')}
                  onMouseLeave={e => !submitting && (e.target.style.background = '#C5A52A')}
                >
                  {submitting
                    ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</span>
                    : 'Submit My Ad for Review →'
                  }
                </button>
              </div>
            </div>

            {/* RIGHT: Live Preview */}
            <div>
              <AdPreview ad={{
                business_name: form.brokerage,
                expert_name:   form.realtor_name,
                description:   form.description,
                image_url:     form.image_url,
                cta_label:     form.cta_label,
                cta_url:       form.cta_url,
              }} />
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RealtorSetup;
